import { Lightning, Router, Storage } from "@lightningjs/sdk";
import { SettingsCarouselItem } from "../layout/SettingsCarouselItem";
import { STORAGE_KEYS, setPinInputCallbacks } from "../../../../../lib/utils";
import { isPostTvLinux, isPostTvAndroid } from "../../../../../lib/utils";
import { postTvWebSocket } from "../../../../../lib/websocket";
import { verifyParentalPin, updateParentalPin, removeParentalPin } from "../../../../../api/mutations";
import { refreshUserData } from "../../../../../lib/utils";

interface SettingsParentalControlTemplateSpec extends Lightning.Component.TemplateSpec {
    Layout: typeof SettingsCarouselItem;
}

export class SettingsParentalControl
    extends Lightning.Component<SettingsParentalControlTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsParentalControlTemplateSpec>
{
    readonly SettingsItem = this.getByRef("Layout")!;

    static override _template(): Lightning.Component.Template<SettingsParentalControlTemplateSpec> {
        return {
            collision: true,
            zIndex: 10,
            Layout: {
                type: SettingsCarouselItem,
                title: "settings.parentControl"
            }
        };
    }

    override _getFocused() {
        // Return the Icon directly so we can handle Enter on it
        // When there are no options, SettingsCarouselItem returns Icon from _getFocused()
        return this.SettingsItem;
    }

    override _setup() {
        this._update();
    }

    override _active() {
        this._update();
    }

    override _handleEnter() {
        // Open PIN input modal on Enter (no arrow icon, no options carousel)
        console.log("[SettingsParentalControl] _handleEnter called");
        this._openPinInput();
        return true; // Indicate we handled the event
    }
    
    override _captureKey(e: KeyboardEvent) {
        // Capture Enter key to ensure it's handled
        if (e.key === "Enter" || e.keyCode === 13) {
            console.log("[SettingsParentalControl] Enter captured");
            this._openPinInput();
            return true;
        }
        return false;
    }

    override _handleRight() {
        // Also allow right arrow to open PIN input modal
        this._openPinInput();
        return true;
    }

    _handleHover() {
        this.fireAncestors("$listItemHover", this);
    }

    _update() {
        // Set title only, don't set options at all (like SettingsAuth)
        // This ensures no arrow icon shows and Enter events bubble properly
        this.SettingsItem.patch({
            title: "settings.parentControl"
            // Don't set options - this matches SettingsAuth pattern
        });
    }

    _hasPin(): boolean {
        // Check if PIN exists in user settings from storage
        const user = Storage.get(STORAGE_KEYS.user) as any;
        return !!(user?.settings?.parentalPin);
    }

    _getPinInputWidget() {
        // Traverse up to find App component (Router.App) and access widgets
        let current: any = this;
        let depth = 0;
        // Go up the component tree to find the App component
        while (current && depth < 20) {
            // Check if this is the App component (has Widgets ref)
            try {
                if (current.getByRef) {
                    const widgets = current.getByRef("Widgets");
                    if (widgets) {
                        const pinInput = widgets.getByRef("PinInput");
                        if (pinInput) {
                            console.log("[SettingsParentalControl] Found PinInput widget at depth", depth);
                            return pinInput;
                        }
                    }
                }
            } catch (e) {
                // Continue searching
            }
            // Move to parent
            current = current.parent;
            depth++;
            // Stop if we've gone too far up (reached application or stage)
            if (!current || current === this.application || current === this.stage) {
                break;
            }
        }
        console.error("[SettingsParentalControl] PinInput widget not found after traversing", depth, "levels");
        return null;
    }

    _openPinInput() {
        console.log("[SettingsParentalControl] _openPinInput called");
        const hasPin = this._hasPin();
        console.log("[SettingsParentalControl] hasPin:", hasPin);
        
        const pinInputWidget = this._getPinInputWidget();

        if (!pinInputWidget) {
            console.error("[SettingsParentalControl] PinInput widget not found - cannot open modal");
            return;
        }

        console.log("[SettingsParentalControl] PinInput widget found, opening...");

        if (hasPin) {
            // PIN exists: First verify existing PIN, then show options (Change PIN / Turn Off)
            console.log("[SettingsParentalControl] PIN exists - opening verify mode first");
            pinInputWidget.open(
                () => {
                    console.log("[SettingsParentalControl] PIN verified successfully, showing options");
                    // On success (PIN verified), show options
                    // PinInput widget handles API verification internally
                    this._showOptions();
                },
                () => {
                    console.log("[SettingsParentalControl] PIN verification cancelled");
                    // On cancel, do nothing - modal will close
                },
                "verify"
            );
        } else {
            // PIN doesn't exist: Set new PIN, then confirm new PIN
            console.log("[SettingsParentalControl] No PIN exists - opening set mode directly");
            this._setNewPin();
        }
    }

    _setNewPin() {
        console.log("[SettingsParentalControl] _setNewPin called");
        const pinInputWidget = this._getPinInputWidget();

        if (!pinInputWidget) {
            console.error("[SettingsParentalControl] PinInput widget not found in _setNewPin");
            return;
        }

        // Add a small delay to ensure the previous modal is fully closed
        setTimeout(() => {
            // Open PIN input for setting new PIN
            // Flow: Set PIN → Confirm PIN → Save PIN
            // The PinInput component handles the transition from "set" to "confirm" mode automatically
            pinInputWidget.open(
                () => {
                    console.log("[SettingsParentalControl] PIN setting flow completed successfully");
                    // This callback is called after PIN is confirmed and matches
                },
                () => {
                    console.log("[SettingsParentalControl] PIN setting cancelled");
                    // On cancel, do nothing - modal will close
                },
                "set",
                (pin: string) => {
                    console.log("[SettingsParentalControl] PIN confirmed and ready to save:", pin);
                    // This callback is called when PIN is confirmed (after set → confirm flow)
                    // After PIN is saved, show age restriction input
                    this._savePin(pin);
                    // Show age restriction input after PIN is saved
                    setTimeout(() => {
                        this._setAgeRestriction();
                    }, 100);
                }
            );
        }, 100);
    }

    async _savePin(pin: string) {
        try {
            // Save PIN via API
            await updateParentalPin(pin);
            console.log("[SettingsParentalControl] PIN saved successfully via API");
            
            // Refresh user data to get updated settings
            await refreshUserData();
            
            // Also save to local storage for backward compatibility
            Storage.set(STORAGE_KEYS.parentalPin, pin);

            // For PostTV Linux, save via WebSocket (parallel update)
            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try {
                    postTvWebSocket.setSettings("pics.pin", pin, () => {});
                } catch (e) {
                    console.error("[SettingsParentalControl] Error saving PIN via WebSocket:", e);
                }
            }

            // For PostTV Android, use Android.setPinCode if available (parallel update)
            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore - setPinCode may not be in type definition
                    if (android && typeof android.setPinCode === "function") {
                        // @ts-ignore
                        android.setPinCode(pin);
                    }
                } catch (e) {
                    console.error("[SettingsParentalControl] Error saving PIN via Android API:", e);
                }
            }
        } catch (error) {
            console.error("[SettingsParentalControl] Error saving PIN via API:", error);
            // Fallback to local storage if API fails
            Storage.set(STORAGE_KEYS.parentalPin, pin);
        }

        this._update();
    }

    _setAgeRestriction() {
        console.log("[SettingsParentalControl] _setAgeRestriction called");
        const pinInputWidget = this._getPinInputWidget();

        if (!pinInputWidget) {
            console.error("[SettingsParentalControl] PinInput widget not found in _setAgeRestriction");
            return;
        }

        // Open PIN input for setting age restriction (1-2 digits, shows numbers)
        pinInputWidget.open(
            () => {
                console.log("[SettingsParentalControl] Age restriction setting completed");
            },
            () => {
                console.log("[SettingsParentalControl] Age restriction setting cancelled");
            },
            "age",
            (age: string) => {
                console.log("[SettingsParentalControl] Age restriction set:", age);
                // Save the age restriction
                this._saveAgeRestriction(age);
            },
            2 // Max 2 digits for age (but can submit with 1 digit)
        );
    }

    async _saveAgeRestriction(age: string) {
        try {
            // Get current PIN to update with age restriction
            const user = Storage.get(STORAGE_KEYS.user) as any;
            const currentPin = user?.settings?.parentalPin || Storage.get(STORAGE_KEYS.parentalPin);
            
            if (currentPin) {
                // Update PIN with age restriction via API
                await updateParentalPin(currentPin, age);
                console.log("[SettingsParentalControl] Age restriction saved successfully via API");
                
                // Refresh user data to get updated settings
                await refreshUserData();
            }
            
            // Also save to local storage for backward compatibility
            Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);

            // For PostTV Linux, save via WebSocket (parallel update)
            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try {
                    postTvWebSocket.setSettings("pics.age", age, () => {});
                } catch (e) {
                    console.error("[SettingsParentalControl] Error saving age restriction via WebSocket:", e);
                }
            }

            // For PostTV Android, use Android API if available (parallel update)
            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore - setAgeRestriction may not be in type definition
                    if (android && typeof android.setAgeRestriction === "function") {
                        // @ts-ignore
                        android.setAgeRestriction(parseInt(age, 10));
                    }
                } catch (e) {
                    console.error("[SettingsParentalControl] Error saving age restriction via Android API:", e);
                }
            }
        } catch (error) {
            console.error("[SettingsParentalControl] Error saving age restriction via API:", error);
            // Fallback to local storage if API fails
            Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);
        }

        this._update();
    }

    _showOptions() {
        console.log("[SettingsParentalControl] _showOptions called");
        const pinInputWidget = this._getPinInputWidget();

        if (!pinInputWidget) {
            console.error("[SettingsParentalControl] PinInput widget not found in _showOptions");
            return;
        }

        // Add a small delay to ensure the previous modal is fully closed
        setTimeout(() => {
            // Open PIN input in options mode to show Change PIN / Turn Off buttons
            pinInputWidget.open(
                () => {
                    console.log("[SettingsParentalControl] Change PIN selected");
                    // User selected "Change PIN" - proceed to set new PIN
                    this._setNewPin();
                },
                () => {
                    console.log("[SettingsParentalControl] Options cancelled");
                    // On cancel, do nothing - modal will close
                },
                "options",
                undefined, // No onPinSet callback for options mode
                undefined, // No digitCount for options mode
                () => {
                    console.log("[SettingsParentalControl] Turn Off Parental Control selected");
                    // User selected "Turn Off" - remove PIN and age restriction
                    this._turnOffParentalControl();
                }
            );
        }, 100);
    }

    async _turnOffParentalControl() {
        console.log("[SettingsParentalControl] _turnOffParentalControl called");
        
        try {
            // Remove PIN via API
            await removeParentalPin();
            console.log("[SettingsParentalControl] PIN removed successfully via API");
            
            // Refresh user data to get updated settings
            await refreshUserData();
            
            // Also remove from local storage for backward compatibility
            Storage.remove(STORAGE_KEYS.parentalPin);
            Storage.remove(STORAGE_KEYS.parentalAgeRestriction);

            // For PostTV Linux, remove via WebSocket (parallel update)
            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try {
                    postTvWebSocket.setSettings("pics.pin", "", () => {});
                    postTvWebSocket.setSettings("pics.age", "", () => {});
                } catch (e) {
                    console.error("[SettingsParentalControl] Error removing PIN via WebSocket:", e);
                }
            }

            // For PostTV Android, use Android API if available (parallel update)
            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore - removePinCode may not be in type definition
                    if (android && typeof android.removePinCode === "function") {
                        // @ts-ignore
                        android.removePinCode();
                    }
                } catch (e) {
                    console.error("[SettingsParentalControl] Error removing PIN via Android API:", e);
                }
            }
        } catch (error) {
            console.error("[SettingsParentalControl] Error removing PIN via API:", error);
            // Fallback: remove from local storage if API fails
            Storage.remove(STORAGE_KEYS.parentalPin);
            Storage.remove(STORAGE_KEYS.parentalAgeRestriction);
        }

        this._update();
    }
}
